import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import DataTable from "../common/DataTable";
import ConfirmModal from "../common/ConfirmModal";
import { RiAddLine, RiMapPinLine, RiArrowRightSLine, RiPencilLine, RiDeleteBinLine } from "@remixicon/react";
import useIsMobile from "../../hooks/useIsMobile";
import PageHeader from "../common/PageHeader";
import Loader from "../common/Loader";
import EmptyState from "../common/EmptyState";
import { getLocations, deleteLocation } from "../../api/locations";

export default function LocationsList() {
	const [loading, setLoading] = useState(true);
	const [locations, setLocations] = useState([]);
	const [toDelete, setToDelete] = useState(null);
	const navigate = useNavigate();
	const isMobile = useIsMobile();

	const load = async () => {
		setLoading(true);
		const res = await getLocations();
		if (res && !res.error) setLocations(res.data || []);
		else toast.error(res?.msg || "Could not load locations");
		setLoading(false);
	};

	useEffect(() => {
		load();
	}, []);

	const handleDelete = async (id) => {
		setToDelete(null);
		const res = await deleteLocation(id);
		if (res && !res.error) {
			toast.success("Location removed");
			load();
		} else {
			toast.error(res?.msg || "Could not remove location");
		}
	};

	const columns = [
		{ name: "Name", selector: (row) => row.locationName, sortable: true, cell: (row) => <Link className="order-link" to={`/locations/${row.id}`}>{row.locationName}</Link> },
		{ name: "Type", selector: (row) => row.locationType?.Title || row.locationType, sortable: true },
		{ name: "Address", selector: (row) => `${row.streetAddress}, ${row.city}, ${row.state} ${row.zip}`, grow: 2 },
		{ name: "Contact", selector: (row) => row.locationContactPersonName },
		{ name: "Phone", selector: (row) => row.locationPhone },
		{
			name: "",
			width: "160px",
			cell: (row) => (
				<div className="d-flex gap-2">
					<Link className="btn btn-sm btn-outline-secondary" to={`/locations/${row.id}/edit`} onClick={(e) => e.stopPropagation()}>Edit</Link>
					<button className="btn btn-sm btn-outline-danger" onClick={(e) => { e.stopPropagation(); setToDelete(row); }}>Delete</button>
				</div>
			),
		},
	];

	return (
		<div>
			<PageHeader
				title="Locations"
				subtitle="Delivery sites where your bids and orders can be fulfilled"
				actions={
					<Link to="/locations/new" className="btn-accent-solid">
						<RiAddLine size={18} /> Add location
					</Link>
				}
			/>
			{loading ? (
				<Loader />
			) : locations.length === 0 ? (
				<EmptyState icon={RiMapPinLine} title="No locations yet" subtitle="Add a delivery site to start requesting bids." action={<Link to="/locations/new" className="btn-accent-solid">Add location</Link>} />
			) : (
				isMobile ? (
					<div className="d-flex flex-column gap-2">
						{locations.map((l) => (
							<div key={l.id} className="card p-3 location-card" onClick={() => navigate(`/locations/${l.id}`)} role="button">
								<div className="d-flex align-items-start gap-3">
									<span className="location-card-icon">
										<RiMapPinLine size={20} />
									</span>
									<div className="flex-grow-1 min-w-0">
										<div className="fw-bold">{l.locationName}</div>
										<div className="text-muted small">{[l.streetAddress, l.city, l.state, l.zip].filter(Boolean).join(", ")}</div>
										{l.locationContactPersonName ? <div className="small mt-1">{l.locationContactPersonName}{l.locationPhone ? ` · ${l.locationPhone}` : ""}</div> : null}
									</div>
									<RiArrowRightSLine size={20} className="text-muted flex-shrink-0" />
								</div>
								<div className="d-flex gap-2 mt-3">
									<Link className="btn-soft flex-fill" to={`/locations/${l.id}/edit`} onClick={(e) => e.stopPropagation()}>
										<RiPencilLine size={16} /> Edit
									</Link>
									<button className="btn-soft flex-fill text-danger" onClick={(e) => { e.stopPropagation(); setToDelete(l); }}>
										<RiDeleteBinLine size={16} /> Delete
									</button>
								</div>
							</div>
						))}
					</div>
				) : (
					<DataTable columns={columns} data={locations} onRowClicked={(row) => navigate(`/locations/${row.id}`)} />
				)
			)}
			{toDelete && (
				<ConfirmModal
					danger
					title="Remove this location?"
					message={`${toDelete.locationName} will no longer be available for new bid requests.`}
					confirmLabel="Remove location"
					onConfirm={() => handleDelete(toDelete.id)}
					onClose={() => setToDelete(null)}
				/>
			)}
		</div>
	);
}
